import { type PlaceholderData } from '@/features/cms/components/CMS/types'
import { CloseIcon, PositiveIcon } from '@shipt/design-system-icons'
import { Column, Row } from '@shipt/design-system-layouts'
import { Body, Headline } from '@shipt/design-system-typography'
import { Table, TableBody, TableCell, TableHead, TableRow } from '@shipt/design-system-tables'
import styled from 'styled-components'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { VisuallyHidden } from '@/features/shared/elements/VisuallyHidden'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'

type StatsItem = {
  feature: string
  personalCheck: boolean
  businessCheck: boolean
}

type StatsItemProps = {
  compareProfileStats: StatsItem[]
}

export const PersonaCompareProfiles = ({
  content_type_id,
  id,
}: Pick<PlaceholderData, 'content_type_id' | 'id'>) => {
  const compareProfileStats: StatsItem[] = [
    {
      feature: '$0 delivery fee on orders over $35',
      personalCheck: true,
      businessCheck: true,
    },
    {
      feature: 'Dedicated customer support',
      personalCheck: true,
      businessCheck: true,
    },
    {
      feature: 'Preferred shoppers',
      personalCheck: true,
      businessCheck: true,
    },
    {
      feature: 'Cashback rewards',
      personalCheck: false,
      businessCheck: true,
    },
    {
      feature: 'Expense tooling',
      personalCheck: false,
      businessCheck: true,
    },
    {
      feature: 'User management',
      personalCheck: false,
      businessCheck: true,
    },
    {
      feature: 'Tax exemption',
      personalCheck: false,
      businessCheck: true,
    },
  ]

  return (
    <SectionContainer {...getCMSWrapperProps({ content_type_id, id })} as="section">
      <CompareProfilesStats compareProfileStats={compareProfileStats} />
    </SectionContainer>
  )
}

const CompareProfilesStats = (compareProfileStats: StatsItemProps) => {
  const { trackCMSElementViewed } = useCMSTrackingEvents()
  const { compareProfileStats: stats } = compareProfileStats

  return (
    <ScrollIntoView
      onInView={() => {
        trackCMSElementViewed({
          content: 'Compare profiles',
        })
      }}
    >
      <Column align="center" marginBottom="xl">
        <Column spacing="sm" align="center" marginBottom="lg">
          <Headline as="h2" size="xl" align="center">
            Compare profiles
          </Headline>
          <Body variant="secondary">
            Business profiles offer different shopping and account management features to meet your
            team&apos;s needs.
          </Body>
        </Column>
      </Column>
      <TableWrapper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell variant="head">
                <Body size="md" weight="medium">
                  Features*
                </Body>
              </TableCell>
              <TableCell variant="head" align="center">
                <Row justify="center" flexGrow={1}>
                  <Body size="md" weight="medium">
                    Personal
                  </Body>
                </Row>
              </TableCell>
              <TableCell variant="head" align="center">
                <Row justify="center" flexGrow={1}>
                  <Body size="md" weight="medium">
                    Business
                  </Body>
                </Row>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stats.map(({ feature, personalCheck, businessCheck }) => (
              <TableRow key={feature}>
                <TableCell>
                  <Body size="md">{feature}</Body>
                </TableCell>
                <TableCell align="center">
                  <Row justify="center" flexGrow={1}>
                    {personalCheck ? (
                      <>
                        <PositiveIcon concept="feedback" feedbackType="positive" />
                        <VisuallyHidden>Personal membership: {feature} is available</VisuallyHidden>
                      </>
                    ) : (
                      <>
                        <CloseIcon concept="feedback" feedbackType="negative" />
                        <VisuallyHidden>
                          Personal membership: {feature} is not available
                        </VisuallyHidden>
                      </>
                    )}
                  </Row>
                </TableCell>
                <TableCell align="center">
                  <Row justify="center" flexGrow={1}>
                    {businessCheck ? (
                      <>
                        <PositiveIcon concept="feedback" feedbackType="positive" />
                        <VisuallyHidden>Business membership: {feature} is available</VisuallyHidden>
                      </>
                    ) : (
                      <>
                        <CloseIcon concept="feedback" feedbackType="negative" />
                        <VisuallyHidden>
                          Business membership: {feature} is not available
                        </VisuallyHidden>
                      </>
                    )}
                  </Row>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableWrapper>
      <Column marginTop="md" align="center">
        <Body emphasis variant="secondary">
          *Features shown included with membership.
        </Body>
      </Column>
    </ScrollIntoView>
  )
}

const SectionContainer = styled(GridLayoutContainer)`
  width: 100%;
`

const TableWrapper = styled(Column)`
  width: 100%;
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.color.border.base.neutral.subtle};
  border-radius: 0.5rem;
`
